import { useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  Firestore,
  type QuerySnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import type { Meeting, RSVP } from '@penticton-dev-events/shared';

import { useFirebaseServices } from '../app/providers/FirebaseProvider';

export type UseAllMeetingsState = {
  readonly meetings: Meeting[];
  readonly loading: boolean;
  readonly error: Error | null;
};

export const useAllMeetings = (): UseAllMeetingsState => {
  const { firestore } = useFirebaseServices();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    console.log('useAllMeetings: Setting up subscription');
    setLoading(true);

    const unsubscribe = subscribeToAllUpcomingMeetings(
      firestore,
      nextMeetings => {
        console.log('useAllMeetings: Received meetings:', nextMeetings);
        setMeetings(nextMeetings);
        setLoading(false);
      },
      subscriptionError => {
        console.error('useAllMeetings: Error fetching meetings:', subscriptionError);
        setError(subscriptionError);
        setLoading(false);
      },
    );

    return () => {
      console.log('useAllMeetings: Unsubscribing');
      unsubscribe();
    };
  }, [firestore]);

  return { meetings, loading, error };
};

const subscribeToAllUpcomingMeetings = (
  firestore: Firestore,
  handleSnapshot: (meetings: Meeting[]) => void,
  handleError: (error: Error) => void,
): Unsubscribe => {
  const now = new Date().toISOString();

  // Use a simple query with just orderBy - filter in client
  const meetingsQuery = query(
    collection(firestore, 'meetings'),
    orderBy('startsAt', 'asc'),
  );

  // Subscribe to RSVPs to calculate accurate attendee counts
  const rsvpsQuery = query(collection(firestore, 'rsvps'));

  let cachedMeetings: Meeting[] = [];
  let cachedRSVPs: RSVP[] = [];

  const updateMeetingsWithCounts = () => {
    // Count approved attending RSVPs per meeting
    const attendeeCounts: Record<string, number> = {};
    cachedRSVPs.forEach(rsvp => {
      if (rsvp.status === 'attending' && rsvp.approvalStatus === 'approved') {
        attendeeCounts[rsvp.meetingId] = (attendeeCounts[rsvp.meetingId] || 0) + 1;
      }
    });

    // Enrich meetings with accurate attendee counts
    const enrichedMeetings = cachedMeetings.map(meeting => ({
      ...meeting,
      attendeeCount: attendeeCounts[meeting.id] || 0,
    }));

    // Filter client-side for upcoming scheduled meetings
    const upcomingMeetings = enrichedMeetings.filter(
      meeting => meeting.startsAt > now && meeting.status === 'scheduled',
    );

    console.log('useAllMeetings: All meetings:', cachedMeetings.length);
    console.log('useAllMeetings: Upcoming meetings:', upcomingMeetings.length);
    console.log('useAllMeetings: Attendee counts:', attendeeCounts);

    handleSnapshot(upcomingMeetings);
  };

  const unsubscribeMeetings = onSnapshot(
    meetingsQuery,
    (snapshot: QuerySnapshot) => {
      cachedMeetings = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Meeting[];

      updateMeetingsWithCounts();
    },
    handleError,
  );

  const unsubscribeRSVPs = onSnapshot(
    rsvpsQuery,
    (snapshot: QuerySnapshot) => {
      cachedRSVPs = snapshot.docs.map(doc => doc.data()) as RSVP[];
      updateMeetingsWithCounts();
    },
    handleError,
  );

  // Return combined unsubscribe function
  return () => {
    unsubscribeMeetings();
    unsubscribeRSVPs();
  };
};
